import React, { type ReactNode } from "react";

import { TYPE_STRING } from "@components/Form";
import InputExtra from "@components/Form/InputExtra";

import {
  type LinkTemplateKey,
  getDefaultLinkTemplatePath,
} from "../../helpers/defaultLinkTemplatePaths";
import getPlaceholderValidationRule from "./validateLinkTemplatePlaceholders";

const LABEL_PORTAL_URL = "Portal";
const LABEL_SIGN_IN_URL = "Sign In";
const LABEL_INVESTMENT_URL = "Investment";
const LABEL_DOCUMENTS_URL = "Documents";
const LABEL_INVESTMENT_SIGN_URL = "PPM Signed";
const LABEL_INVESTMENT_SIGN_THANK_YOU_URL = "PPM Signed - Thank You";
const LABEL_SOFT_COMMITMENT_URL = "Soft Commitment";
const EXTRA_PORTAL_URL = "The home page of your portal. The emails that do not point to one project use this URL, for example the password reset email.";
const EXTRA_SIGN_IN_URL = "The sign in page of your portal. The emails that ask the investor to sign in use this URL, for example the distribution on hold email.";
const EXTRA_INVESTMENT_URL = "Your page that starts the investment in one project. Put {projectId} into the URL.";
const EXTRA_DOCUMENTS_URL = "Your page that shows the documents of one project. Put {projectId} into the URL.";
const EXTRA_INVESTMENT_SIGN_URL = "The page that DocuSign returns the investor to after the investor signs in your portal. Your page must handle ?event=signing_complete and ?event=ttl_expired. Put {projectId} and {investmentId} into the URL.";
const EXTRA_INVESTMENT_SIGN_THANK_YOU_URL = "The page that DocuSign returns the investor to after an operator sends the documents. This page is public, and the investor does not sign in. Your page must handle ?event=signing_complete and ?event=ttl_expired. You can put {projectId} and {investmentId} into the URL.";
const EXTRA_SOFT_COMMITMENT_URL = "Your public soft commitment form. Put {projectId} and {sponsorId} into the URL.";

type LinkTemplateField = {
  name: LinkTemplateKey;
  label: string;
  extra: string;
  placeholders?: string[];
};

type LinkTemplateInput = {
  name: string;
  type: string;
  label: string;
  placeholder: string;
  itemProps: {
    extra: ReactNode;
    rules?: ReturnType<typeof getPlaceholderValidationRule>[];
  };
};

const LINK_TEMPLATE_FIELDS: LinkTemplateField[] = [
  { name: "portalUrl", label: LABEL_PORTAL_URL, extra: EXTRA_PORTAL_URL },
  {
    name: "softCommitmentUrl",
    label: LABEL_SOFT_COMMITMENT_URL,
    extra: EXTRA_SOFT_COMMITMENT_URL,
    placeholders: ["{projectId}", "{sponsorId}"],
  },
  { name: "signInUrl", label: LABEL_SIGN_IN_URL, extra: EXTRA_SIGN_IN_URL },
  {
    name: "investmentUrl",
    label: LABEL_INVESTMENT_URL,
    extra: EXTRA_INVESTMENT_URL,
    placeholders: ["{projectId}"],
  },
  {
    name: "documentsUrl",
    label: LABEL_DOCUMENTS_URL,
    extra: EXTRA_DOCUMENTS_URL,
    placeholders: ["{projectId}"],
  },
  {
    name: "investmentSignUrl",
    label: LABEL_INVESTMENT_SIGN_URL,
    extra: EXTRA_INVESTMENT_SIGN_URL,
    placeholders: ["{projectId}", "{investmentId}"],
  },
  {
    name: "investmentSignThankYouUrl",
    label: LABEL_INVESTMENT_SIGN_THANK_YOU_URL,
    extra: EXTRA_INVESTMENT_SIGN_THANK_YOU_URL,
  },
];

const getLinkTemplatesSchema = (domain?: string): LinkTemplateInput[] =>
  LINK_TEMPLATE_FIELDS.map(({ name, label, extra, placeholders }) => {
    const placeholder = getDefaultLinkTemplatePath(name, domain);

    return {
      name,
      type: TYPE_STRING,
      label,
      placeholder,
      itemProps: {
        extra: <InputExtra text={extra} hasIcon={false} />,
        ...(placeholders && {
          rules: [getPlaceholderValidationRule(placeholders)],
        }),
      },
    };
  });

export default getLinkTemplatesSchema;
